import { z } from "zod";

import type {
  UpdatePromotedCategoriesResult,
  UpdateTaxonomyOnboardingResult,
} from "@/core/repositories/interfaces/ITaxonomyRepository";
import type { ITaxonomyRepository } from "@/core/repositories/interfaces/ITaxonomyRepository";
import type { ITaxonomyService } from "@/core/services/interfaces/ITaxonomyService";
import { sortTaxonomyByLabel } from "@/core/services/taxonomy.sort";
import {
  PROMOTED_CATEGORY_TYPES,
  type PromotedCategory,
} from "@/core/services/types/promoted-category.types";
import type {
  TaxonomyCategory,
  TaxonomyData,
  TaxonomyItem,
} from "@/core/services/types/taxonomy.types";

const updateOnboardingSchema = z.object({
  id: z.string().uuid(),
  onboarding: z.boolean(),
});

const promotedCategorySchema = z.object({
  category: z.string().trim().min(1),
  img: z.string().trim().url(),
  type: z.enum(PROMOTED_CATEGORY_TYPES),
});

const replacePromotedSchema = z.array(promotedCategorySchema);

function byCategory(
  items: readonly TaxonomyItem[],
  category: TaxonomyCategory,
): TaxonomyItem[] {
  return sortTaxonomyByLabel(
    items.filter((item) => item.category === category),
  );
}

export class TaxonomyService implements ITaxonomyService {
  constructor(private readonly repository: ITaxonomyRepository) {}

  async listAll(): Promise<TaxonomyData> {
    const items = await this.repository.findAll();

    return {
      artForms: byCategory(items, "ART_FORM"),
      culturalAreas: byCategory(items, "CULTURAL_AREA"),
      artisticPeriods: byCategory(items, "ARTISTIC_PERIOD"),
      exhibitionTypes: byCategory(items, "EXHIBITION_TYPE"),
    };
  }

  async updateOnboarding(
    id: string,
    onboarding: boolean,
  ): Promise<UpdateTaxonomyOnboardingResult> {
    const parsed = updateOnboardingSchema.safeParse({ id, onboarding });
    if (!parsed.success) {
      console.error(
        "[TaxonomyService.updateOnboarding] validation",
        parsed.error.flatten(),
      );
      return { success: false, error: "Invalid onboarding payload" };
    }

    return this.repository.updateOnboarding(
      parsed.data.id,
      parsed.data.onboarding,
    );
  }

  async listPromotedCategories(): Promise<PromotedCategory[]> {
    return this.repository.getPromotedCategories();
  }

  async replacePromotedCategories(
    items: PromotedCategory[],
  ): Promise<UpdatePromotedCategoriesResult> {
    const parsed = replacePromotedSchema.safeParse(items);
    if (!parsed.success) {
      console.error(
        "[TaxonomyService.replacePromotedCategories] validation",
        parsed.error.flatten(),
      );
      return { success: false, error: "Invalid promoted categories payload" };
    }

    return this.repository.replacePromotedCategories(parsed.data);
  }
}
